import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Modal, Alert, Loader, Grid, Card, Badge, Avatar,
    Accordion, Stack, Paper, Text, Title, List, Group
} from '@mantine/core';
import {
    IconUser, IconX, IconChevronDown, IconMail, IconPhone, IconHome,
    IconBriefcase, IconId, IconCalendar, IconMapPin, IconShield,
    IconBuilding, IconUsers, IconCreditCard
} from '@tabler/icons-react';
import { LoadingState } from './common/LoadingState';
import { MembershipListViewer } from './common/MembershipListViewer';
import { JsonViewer } from './common/JsonViewer';
import MeService from '../services/MeService';
import { formatDateShort } from '../utils/dateTimeUtils';

const renderUserData = (data: any) => {
    if (!data) return null;

    return (
        <Box>
            <Card mb="lg" withBorder bg="blue.6" c="white">
                <Card.Section p="md">
                    <Group gap="md" align="center">
                        <Avatar size={64} radius="xl" color="white" c="blue.6">
                            <IconUser size={32} />
                        </Avatar>
                        <Box>
                            <Title order={4} c="white" mb="xs">
                                {data.DisplayName}
                            </Title>
                            <Text size="lg" opacity={0.9}>
                                {data.JobTitle} • {data.Department}
                            </Text>
                            <Group gap="sm" mt="sm">
                                <Badge size="sm" variant="light" color="white">
                                    {data.EntityType}
                                </Badge>
                                <Badge size="sm" variant="light" color="white">
                                    {data.ApiRole}
                                </Badge>
                            </Group>
                        </Box>
                    </Group>
                </Card.Section>
            </Card>

            <Grid gutter="lg" mb="lg">
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card withBorder>
                        <Card.Section p="md">
                            <Title order={5} mb="sm">
                                <Group gap="sm" align="center">
                                    <IconId size={20} />
                                    Personal Information
                                </Group>
                            </Title>
                            <List listStyleType="none">
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconMail size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Email</Text>
                                            <Text size="sm">{data.Email}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconMail size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Personal Email</Text>
                                            <Text size="sm">{data.PersonalEmail}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconPhone size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Phone</Text>
                                            <Text size="sm">{data.HomePhone}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconCalendar size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Date of Birth</Text>
                                            <Text size="sm">{formatDateShort(data.Dob)}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                            </List>
                        </Card.Section>
                    </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card withBorder>
                        <Card.Section p="md">
                            <Title order={5} mb="sm">
                                <Group gap="sm" align="center">
                                    <IconBriefcase size={20} />
                                    Work Information
                                </Group>
                            </Title>
                            <List listStyleType="none">
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconId size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Employee ID</Text>
                                            <Text size="sm">{data.Id}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconBriefcase size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Job Code</Text>
                                            <Text size="sm">{data.JobCode}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconCalendar size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Start Date</Text>
                                            <Text size="sm">{formatDateShort(data.StartDate)}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconBuilding size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Manager ID</Text>
                                            <Text size="sm">{data.Manager}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                            </List>
                        </Card.Section>
                    </Card>
                </Grid.Col>
            </Grid>

            <Card withBorder mb="lg">
                <Card.Section p="md">
                    <Title order={5} mb="sm">
                        <Group gap="sm" align="center">
                            <IconHome size={20} />
                            Address Information
                        </Group>
                    </Title>
                    <Group gap="sm" align="center" mb="md">
                        <IconMapPin size={18} style={{ flexShrink: 0 }} />
                        <Text size="sm">
                            {data.HomeAddress}, {data.HomeCity}, {data.HomeState} {data.HomeZip}
                        </Text>
                    </Group>
                </Card.Section>
            </Card>

            <Grid gutter="lg" mb="lg">
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card withBorder>
                        <Card.Section p="md">
                            <Title order={5} mb="sm">
                                <Group gap="sm" align="center">
                                    <IconShield size={20} />
                                    Identity & Access
                                </Group>
                            </Title>
                            <List listStyleType="none">
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconId size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Account Name</Text>
                                            <Text size="sm">{data.AccountName}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconShield size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Entra ID</Text>
                                            <Text size="sm">{data.EntraId}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconShield size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Object ID</Text>
                                            <Text size="sm">{data.ObjectId}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconCreditCard size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Card Data</Text>
                                            <Text size="sm">{data.CardData}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                            </List>
                        </Card.Section>
                    </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <Card withBorder>
                        <Card.Section p="md">
                            <Title order={5} mb="sm">
                                <Group gap="sm" align="center">
                                    <IconBuilding size={20} />
                                    Organization
                                </Group>
                            </Title>
                            <List listStyleType="none">
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconBuilding size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Responsibility Centers</Text>
                                            <Text size="sm">{data.Rcs?.join(', ')}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconBriefcase size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">RC Job Codes</Text>
                                            {data.RcJobCodes && data.RcJobCodes.length > 0 ? (
                                                <Group gap="xs" mt="xs" wrap="wrap">
                                                    {data.RcJobCodes.map((jobCode: string, index: number) => (
                                                        <Badge
                                                            key={index}
                                                            size="sm"
                                                            variant="light"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                const filter = `JobCode eq "${jobCode}"`;
                                                                const encodedFilter = encodeURIComponent(filter);
                                                                window.location.hash = `#Person?filter=${encodedFilter}`;
                                                            }}
                                                        >
                                                            {jobCode}
                                                        </Badge>
                                                    ))}
                                                </Group>
                                            ) : (
                                                <Text size="sm">N/A</Text>
                                            )}
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconId size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">Position</Text>
                                            <Text size="sm">{data.Position}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                                <List.Item>
                                    <Group gap="sm" mb="sm" wrap="nowrap">
                                        <IconId size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                                        <Box>
                                            <Text size="xs" c="dimmed">RC</Text>
                                            <Text size="sm">{data.Rc}</Text>
                                        </Box>
                                    </Group>
                                </List.Item>
                            </List>
                        </Card.Section>
                    </Card>
                </Grid.Col>
            </Grid>

            <Accordion defaultValue="groups">
                <Accordion.Item value="groups">
                    <Accordion.Control>
                        <Group gap="sm" align="center">
                            <IconUsers size={20} />
                            Group Memberships ({data.MemberOf?.length || 0} direct, {data.MemberOfTransitive?.length || 0} transitive)
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <Grid gutter="md">
                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <MembershipListViewer
                                    memberships={data.MemberOf || []}
                                    title="Direct Memberships"
                                    subtitle="Groups this user is directly a member of"
                                    maxHeight={200}
                                    showCount={true}
                                    clickable={true}
                                    enableSearch={true}
                                    enableCopy={true}
                                    enableDetails={true}
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <MembershipListViewer
                                    memberships={data.MemberOfTransitive || []}
                                    title="Transitive Memberships"
                                    subtitle="Groups this user is a member of through inheritance"
                                    maxHeight={200}
                                    showCount={true}
                                    clickable={true}
                                    enableSearch={true}
                                    enableCopy={true}
                                    enableDetails={true}
                                />
                            </Grid.Col>
                        </Grid>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>

            <Accordion>
                <Accordion.Item value="access">
                    <Accordion.Control>
                        <Group gap="sm" align="center">
                            <IconShield size={20} />
                            Access & Permissions
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <Grid gutter="md">
                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <Text size="sm" fw={500} c="blue" mb="xs">
                                    Access Weekdays
                                </Text>
                                <Group gap="sm" wrap="wrap">
                                    {data.AccessWeekday?.map((access: string, index: number) => (
                                        <Badge key={index} size="sm" variant="outline" color="blue">
                                            {access}
                                        </Badge>
                                    ))}
                                </Group>
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <Text size="sm" fw={500} c="blue" mb="xs">
                                    System Flags
                                </Text>
                                <Stack gap="sm">
                                    <Badge
                                        size="sm"
                                        color={data.IsCharter ? 'green' : 'gray'}
                                    >
                                        Is Charter: {data.IsCharter ? 'Yes' : 'No'}
                                    </Badge>
                                    <Badge
                                        size="sm"
                                        color={data.SapUser ? 'green' : 'gray'}
                                    >
                                        SAP User: {data.SapUser ? 'Yes' : 'No'}
                                    </Badge>
                                </Stack>
                            </Grid.Col>
                        </Grid>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>

            <Accordion>
                <Accordion.Item value="properties">
                    <Accordion.Control>
                        <Group gap="sm" align="center">
                            <IconId size={20} />
                            All Properties
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <Grid gutter="md">
                            {Object.entries(data)
                                .filter(([key]) => {
                                    const shownProperties = [
                                        'DisplayName', 'JobTitle', 'Department', 'EntityType', 'ApiRole',
                                        'Email', 'PersonalEmail', 'HomePhone', 'Dob',
                                        'Id', 'JobCode', 'StartDate', 'Manager',
                                        'HomeAddress', 'HomeCity', 'HomeState', 'HomeZip',
                                        'AccountName', 'EntraId', 'ObjectId', 'CardData',
                                        'Rcs', 'RcJobCodes', 'Position', 'Rc',
                                        'MemberOf', 'MemberOfTransitive',
                                        'AccessWeekday', 'IsCharter', 'SapUser'
                                    ];
                                    return !shownProperties.includes(key);
                                })
                                .map(([key, value]) => (
                                    <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={key}>
                                        <Paper p="sm" withBorder>
                                            <Text size="xs" c="dimmed" display="block" mb="xs">
                                                {key}
                                            </Text>
                                            <Text size="sm" ff={typeof value === 'string' && (value.includes('{') || value.includes('[')) ? 'monospace' : undefined} style={{ wordBreak: 'break-word' }}>
                                                {typeof value === 'object' && value !== null
                                                    ? JSON.stringify(value, null, 2)
                                                    : String(value ?? 'N/A')}
                                            </Text>
                                        </Paper>
                                    </Grid.Col>
                                ))}
                        </Grid>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>

            <Accordion>
                <Accordion.Item value="raw">
                    <Accordion.Control>
                        <Group gap="sm" align="center">
                            <IconId size={20} />
                            Raw JSON Data
                        </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <JsonViewer data={data} />
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>
        </Box>
    );
};

export interface EntitiesMeDialogProps {
    open: boolean;
    onClose: () => void;
}

/** Dialog that shows entities/me data. Can be opened from UserInfo or elsewhere. */
export const EntitiesMeDialog: React.FC<EntitiesMeDialogProps> = ({ open, onClose }) => {
    const [meData, setMeData] = useState<any>(null);
    const [meLoading, setMeLoading] = useState(false);
    const [meError, setMeError] = useState<string | null>(null);

    useEffect(() => {
        const unsubscribe = MeService.subscribe(() => {
            setMeData(MeService.getMe());
            setMeLoading(MeService.isLoading());
            setMeError(MeService.getError());
        });
        return unsubscribe;
    }, []);

    const fetchMe = useCallback(async () => {
        const cached = MeService.getMe();
        if (cached) {
            setMeData(cached);
            setMeLoading(false);
        } else {
            try {
                const data = await MeService.fetchMe();
                if (data) setMeData(data);
            } catch {
                // Error handled via MeService subscription
            }
        }
    }, []);

    useEffect(() => {
        if (open) {
            fetchMe();
        }
    }, [open, fetchMe]);

    return (
        <Modal
            opened={open}
            onClose={onClose}
            size="xl"
            title={
                <Group gap="sm" align="center">
                    <IconUser size={24} />
                    Current User (entities/me)
                </Group>
            }
            styles={{ body: { minHeight: '80vh', maxHeight: '90vh' } }}
        >
            {meLoading && !meData && (
                <LoadingState
                    variant="centered"
                    size={40}
                    label="Loading current user data..."
                    minHeight={300}
                />
            )}
            {meError && (
                <Alert color="red" mb="md">
                    <Text size="sm">
                        <strong>Error:</strong> {meError}
                    </Text>
                </Alert>
            )}
            {meData && (
                <Box>
                    <Alert color="green" mb="md">
                        <Text size="sm">
                            <strong>Success!</strong> Current user data retrieved successfully
                        </Text>
                    </Alert>
                    {renderUserData(meData)}
                </Box>
            )}
            <Group gap="sm" justify="flex-end" mt="md">
                <Button onClick={onClose}>Close</Button>
                {meData && (
                    <Button onClick={fetchMe} variant="outline" disabled={meLoading}>
                        Refresh
                    </Button>
                )}
            </Group>
        </Modal>
    );
};

export const EntitiesMeButton: React.FC = () => {
    const [meDialogOpen, setMeDialogOpen] = useState(false);
    return (
        <>
            <Button
                variant="outline"
                size="xs"
                leftSection={<IconUser size={16} />}
                onClick={() => setMeDialogOpen(true)}
                fz="xs"
                py="xs"
                px="sm"
                style={{
                    minWidth: 'auto',
                    color: 'inherit',
                    borderColor: 'currentColor'
                }}
            >
                entities/me
            </Button>
            <EntitiesMeDialog open={meDialogOpen} onClose={() => setMeDialogOpen(false)} />
        </>
    );
};
